import { type CSSProperties, useCallback, useEffect, useMemo, useState } from 'react';

import { ExclamationTriangleIcon, InformationCircleIcon, XCircleIcon } from '@heroicons/react/20/solid';

import { currencyFormatter } from '@/utils/currencyFormatter';

import type { GroupSubscriptionData } from '../groupSubscriptionTypes';
import { ChipInput, type ChipValidator } from './ChipInput';
import { normalizeEmail, validateEmail } from './chipValidators';
import { ManageSeatsLink } from './ManageSeatsLink';
import { seatsRemaining as computeSeatsRemaining } from './seatMath';

type InviteByEmailModalProps = {
  isOpen: boolean;
  group: GroupSubscriptionData | null;
  onCancel: () => void;
  /**
   * Send invites for the supplied emails. Real wiring lands with `CreateInvitation`
   * (BEE-18931). For now the call site closes the modal and resets state.
   */
  onSubmit?: (groupId: string, emails: string[]) => void;
  /**
   * The over-seats path. When the typed list exceeds remaining capacity, the
   * primary CTA flips to "Add N seats and invite" and dispatches here. Real
   * wiring is the C3 Granola flow owned by BEE-18971; for now this is a no-op
   * placeholder that closes the modal.
   */
  onAddSeatsAndInvite?: (groupId: string, emails: string[], extraSeats: number) => void;
  onOpenChange: (open: boolean) => void;
  isLoading?: boolean;
  /** Opens the Manage Seats modal from the inline link next to "X seats remaining".
   *  Single-slot modal state in the host means this replaces (not stacks) this modal. */
  onManageSeats?: () => void;
  /**
   * Whether inviting beyond remaining capacity may purchase seats. Deferred for
   * MVP on custom-deal groups — no charging the admin without
   * confirmation. When false, over-capacity is a hard block; the admin must
   * invite within the seats already available.
   */
  canAddSeats?: boolean;
  /** i18n lookup dict — same shape used by GroupSubscriptionsElement. Only
   *  `group_subscriptions.card.manage_seats` is consumed here. */
  translations?: Record<string, string>;
};

/**
 * Set of emails that already hold or are reserving a seat. Used to surface
 * duplicate chips with a warning state — the user should know we're not going
 * to send a second invite to someone who's already in.
 */
function buildOccupiedEmailSet(group: GroupSubscriptionData): Set<string> {
  const occupied = new Set<string>();
  for (const seat of group.seats) {
    if (seat.email && (seat.status === 'filled' || seat.status === 'terminating' || seat.status === 'invited')) {
      occupied.add(seat.email.toLowerCase());
    }
  }
  return occupied;
}

export function InviteByEmailModal({
  isOpen,
  group,
  onCancel,
  onSubmit,
  onAddSeatsAndInvite,
  onOpenChange,
  isLoading = false,
  onManageSeats,
  canAddSeats = true,
  translations,
}: InviteByEmailModalProps) {
  const [emails, setEmails] = useState<string[]>([]);

  // ESC closes. Outside-click intentionally NOT bound — clicking the overlay
  // shouldn't dump a long pasted list of emails.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onOpenChange(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onOpenChange]);

  const occupied = useMemo(() => (group ? buildOccupiedEmailSet(group) : new Set<string>()), [group]);

  /**
   * Per-chip validator. Order matters — typos surface first, then in-list dupes,
   * then occupied-seat warnings. The component never silently drops; everything
   * that fails a check renders as a red or amber chip.
   */
  const validate: ChipValidator = useCallback(
    (email, ctx) => {
      const formatResult = validateEmail(email);
      if (typeof formatResult === 'object' && formatResult.state === 'invalid') return formatResult;
      if (ctx && ctx.values.indexOf(email) < ctx.index) {
        return { state: 'invalid', message: 'Duplicate' };
      }
      if (occupied.has(email)) return { state: 'warning', message: 'Already invited or filled' };
      return 'valid';
    },
    [occupied],
  );

  // Same math as the GroupCard's "X seats remaining" badge — both views must
  // show the same number, AND the number must be accurate. Pending invites
  // reserve a seat (see seatMath.ts), so the cap is `filled + terminating + pending`.
  const seatsRemaining = useMemo(() => (group ? computeSeatsRemaining(group) : 0), [group]);

  /**
   * Single classifier pass: bucket each chip into one of four categories. Drives
   * BOTH the banner content (counts per category) and the sendable list (anything
   * not blocked). Order mirrors `validate` — format check first, then in-list
   * dupes, then occupied seats — so a malformed email never gets misreported as
   * a duplicate of itself.
   */
  const breakdown = useMemo(() => {
    const result = {
      invalidCount: 0,
      duplicateCount: 0,
      occupiedCount: 0,
      sendable: [] as string[],
    };
    emails.forEach((email, idx) => {
      const formatResult = validateEmail(email);
      if (typeof formatResult === 'object' && formatResult.state === 'invalid') {
        result.invalidCount += 1;
        return;
      }
      if (emails.indexOf(email) < idx) {
        result.duplicateCount += 1;
        return;
      }
      if (occupied.has(email)) {
        result.occupiedCount += 1;
        result.sendable.push(email); // BE dedupes; we still send to keep the list complete
        return;
      }
      result.sendable.push(email);
    });
    return result;
  }, [emails, occupied]);

  const sendable = breakdown.sendable;

  if (!isOpen || !group) return null;

  const sendableCount = sendable.length;
  const exceedsCapacity = sendableCount > seatsRemaining;
  const extraSeatsNeeded = Math.max(0, sendableCount - seatsRemaining);
  // Over-capacity is a hard block when seat additions are disabled (MVP).
  const overCapacityBlocked = exceedsCapacity && !canAddSeats;
  const hasBlockers = breakdown.invalidCount > 0 || breakdown.duplicateCount > 0;
  const showBanner = hasBlockers || breakdown.occupiedCount > 0;

  const submitLabel = (() => {
    if (sendableCount === 0) return 'Send invite';
    if (exceedsCapacity && canAddSeats)
      return `Add ${extraSeatsNeeded} ${extraSeatsNeeded === 1 ? 'seat' : 'seats'} and invite`;
    return `Send ${sendableCount} ${sendableCount === 1 ? 'invite' : 'invites'}`;
  })();

  // Errors block submission — fix-or-remove is required before sending.
  const canSubmit = !isLoading && sendableCount > 0 && !hasBlockers && !overCapacityBlocked;

  const handleSubmit = () => {
    if (!canSubmit) return;
    if (exceedsCapacity) {
      onAddSeatsAndInvite?.(group.id, sendable, extraSeatsNeeded);
    } else {
      onSubmit?.(group.id, sendable);
    }
  };

  const overlayStyle: CSSProperties = { backgroundColor: '#00000030' };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="invite-email-title"
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={overlayStyle}
    >
      <div className="flex w-full flex-col gap-6 overflow-hidden bg-white p-6 shadow-lg sm:w-[640px] sm:rounded-md">
        <div className="flex flex-col gap-1">
          <h1 id="invite-email-title" className="text-lg font-medium text-gray-900">
            Invite by email address
          </h1>
          <p className="text-sm text-gray-500">
            {seatsRemaining} {seatsRemaining === 1 ? 'seat' : 'seats'} remaining
            {sendableCount > 0 && !exceedsCapacity && (
              <>
                {' '}
                (inviting {sendableCount} of {seatsRemaining})
              </>
            )}
            {onManageSeats && (
              <>
                {'. '}
                <ManageSeatsLink
                  onClick={onManageSeats}
                  label={translations?.['group_subscriptions.card.manage_seats'] ?? 'Manage seats'}
                />
              </>
            )}
          </p>
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="invite-email-chips" className="text-sm font-medium text-gray-900">
            Email addresses
          </label>
          <ChipInput
            id="invite-email-chips"
            value={emails}
            onChange={setEmails}
            validate={validate}
            normalize={normalizeEmail}
            placeholder="Add email addresses, comma separated"
            disabled={isLoading}
            inputProps={{
              // Intentionally NOT type="email" — browsers strip trailing whitespace
              // from email inputs before onChange fires, which breaks space-as-delimiter.
              // We do our own validation via the `validate` prop.
              inputMode: 'email',
              autoComplete: 'off',
              autoCapitalize: 'off',
              spellCheck: false,
            }}
          />
        </div>

        {showBanner && (
          <div
            className={
              hasBlockers
                ? 'flex gap-3 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-900'
                : 'flex gap-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900'
            }
          >
            {hasBlockers ? (
              <XCircleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-500" aria-hidden="true" />
            ) : (
              <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-500" aria-hidden="true" />
            )}
            <div className="flex flex-col gap-1">
              <p className="font-medium">
                {hasBlockers ? "Some addresses can't be invited" : 'Some addresses are already invited'}
              </p>
              <ul className="ml-4 list-disc space-y-0.5">
                {breakdown.invalidCount > 0 && (
                  <li>
                    {breakdown.invalidCount} invalid email {breakdown.invalidCount === 1 ? 'address' : 'addresses'} —
                    fix the format
                  </li>
                )}
                {breakdown.duplicateCount > 0 && (
                  <li>
                    {breakdown.duplicateCount} duplicate {breakdown.duplicateCount === 1 ? 'email' : 'emails'} — remove
                    the {breakdown.duplicateCount === 1 ? 'repeat' : 'repeats'}
                  </li>
                )}
                {breakdown.occupiedCount > 0 && (
                  <li>
                    {breakdown.occupiedCount} {breakdown.occupiedCount === 1 ? 'address is' : 'addresses are'} already
                    invited or filled — {breakdown.occupiedCount === 1 ? 'it' : 'they'} will be skipped
                  </li>
                )}
              </ul>
            </div>
          </div>
        )}

        {exceedsCapacity && !hasBlockers && canAddSeats && (
          <div className="flex gap-3 rounded-md border border-indigo-200 bg-indigo-50 p-3 text-sm text-indigo-900">
            <InformationCircleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-indigo-500" aria-hidden="true" />
            <div className="flex flex-col gap-1">
              <p className="font-medium">
                You need to purchase {extraSeatsNeeded} more {extraSeatsNeeded === 1 ? 'seat' : 'seats'} to invite
                everyone
              </p>
              <p>
                Add {extraSeatsNeeded} more {extraSeatsNeeded === 1 ? 'seat' : 'seats'} at{' '}
                {currencyFormatter({ value: group.per_seat_amount_cents / 100, currency: group.currency })} per seat/
                {group.interval}, or remove {extraSeatsNeeded}{' '}
                {extraSeatsNeeded === 1 ? 'email address' : 'email addresses'}.
              </p>
            </div>
          </div>
        )}

        {overCapacityBlocked && !hasBlockers && (
          <div className="flex gap-3 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-900">
            <XCircleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-500" aria-hidden="true" />
            <div className="flex flex-col gap-1">
              <p className="font-medium">Not enough seats available</p>
              <p>
                This group has {seatsRemaining} open {seatsRemaining === 1 ? 'seat' : 'seats'}. Remove{' '}
                {extraSeatsNeeded} {extraSeatsNeeded === 1 ? 'email address' : 'email addresses'} to invite within the
                seats available, or contact your publisher to add more seats.
              </p>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-3">
          <button
            type="button"
            disabled={isLoading}
            onClick={onCancel}
            className="rounded-md border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-900 shadow-sm hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!canSubmit}
            onClick={handleSubmit}
            className="rounded-md bg-wt-primary px-3 py-2 text-sm font-medium text-wt-text-on-primary shadow-sm hover:opacity-90 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-500 disabled:hover:opacity-100"
          >
            {submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
